import React, { useState } from 'react';

const SDKSection = ({ isDarkMode = true }) => {
  const [activeTab, setActiveTab] = useState('main');

  // Custom syntax highlighting function with added gap for output sections
  const highlightCode = (code) => {
    const lines = code.split('\n');
    const result = [];
    let inOutputBlock = false;
    let outputGapAdded = false;
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      
      // Check if we're entering output section
      if (line.includes('"result"') || line.includes('// Output')) {
        inOutputBlock = true;
        // Add gap only once
        if (!outputGapAdded) {
          result.push(<div key="output-gap" className="output-gap"></div>);
          outputGapAdded = true;
        }
        result.push(
          <div key={i} className="code-line">
            <span style={{ color: '#D4D4D4' }}>{line}</span>
          </div>
        );
        continue;
      }

      // If in output block, keep text white
      if (inOutputBlock) {
        result.push(
          <div key={i} className="code-line">
            <span style={{ color: '#D4D4D4' }}>{line}</span>
          </div>
        );
        continue;
      }

      // Handle comments
      if (line.trim().startsWith('//') || line.trim().startsWith('#')) {
        result.push(
          <div key={i} className="code-line">
            <span style={{ color: '#6A9955', fontStyle: 'italic' }}>{line}</span>
          </div>
        );
        continue;
      }

      // Create React element directly instead of using dangerouslySetInnerHTML
      const parts = [];
      let currentIndex = 0;

      // Match different code elements
      const matches = [
        ...line.matchAll(/\b(\d+)\b/g), // numbers
        ...line.matchAll(/"([^"]*)"/g), // strings
        ...line.matchAll(/\b(print|len|range|str|int|float)\b/g), // built-ins
        ...line.matchAll(/\b(from|import|def|return|if|else|lambda|class|for|while|in|as)\b/g), // keywords
        ...line.matchAll(/\.([a-zA-Z_]\w*)\(/g) // function calls
      ].sort((a, b) => a.index - b.index);

      matches.forEach(match => {
        if (match.index > currentIndex) {
          parts.push(line.substring(currentIndex, match.index));
        }

        const text = match[0];
        let color;

        if (/^\d+$/.test(text)) color = '#B5CEA8'; // numbers
        else if (text.startsWith('"')) color = '#CE9178'; // strings
        else if (/^(print|len|range|str|int|float)$/.test(text)) color = '#4EC9B0'; // built-ins
        else if (/^(from|import|def|return|if|else|lambda|class|for|while|in|as)$/.test(text)) color = '#569CD6'; // keywords
        else if (/\.[a-zA-Z_]\w*\($/.test(text)) color = '#DCDCAA'; // function calls

        parts.push(<span key={match.index} style={{ color }}>{text}</span>);
        currentIndex = match.index + text.length;
      });

      if (currentIndex < line.length) {
        parts.push(line.substring(currentIndex));
      }

      result.push(
        <div key={i} className="code-line">
          {parts}
        </div>
      );
    }
    
    return result;
  };

  const codeExamples = {
    main: `//INITIALIZE SIMULATOR AND ALGORITHM OF THE SDK
    
from revren_sdk.quantum_algorithms import QuantumAlgorithms
from revren_sdk.quantum_simulator import QuantumSimulator
from revren_sdk.quantum_circuit import QuantumCircuit

# Initialize the Quantum Simulator
simulator = QuantumSimulator()

# Initialize the Quantum Algorithms with the simulator
qa = QuantumAlgorithms(simulator)
`,

    algorithm1: `// SIMON ALGORITHM
from revren_sdk.quantum_algorithms import QuantumAlgorithms
from revren_sdk.quantum_simulator import QuantumSimulator
from revren_sdk.quantum_circuit import QuantumCircuit
num_qubits = 3  # Set number of qubits
oracle_function = lambda x: x  # Replace with actual oracle if needed
result = qa.simon_algorithm(num_qubits, oracle_function)
print("Simon's Algorithm Output:", result)

// Output
{
  "result": Applying Hadamard gate to qubit 0.
Applying Hadamard gate to qubit 1.
Applying Hadamard gate to qubit 2.
Applying Hadamard gate to qubit 0.
Applying Hadamard gate to qubit 1.
Applying Hadamard gate to qubit 2.
Running quantum circuit simulation...
Quantum Circuit:
('H', 0)
('H', 1)
('H', 2)
('H', 0)
('H', 1)
('H', 2)
Measuring all qubits...
Simon's Algorithm Output: [0, 0, 0, 0, 0, 0]
}`,

    algorithm2: `//GROVER ALGORITHM
from revren_sdk.quantum_algorithms import QuantumAlgorithms
from revren_sdk.quantum_simulator import QuantumSimulator
from revren_sdk.quantum_circuit import QuantumCircuit
def oracle_function(state):
"""Oracle function that marks the target state."""
return 1 if state == "101" else 0

num_qubits = 3
result = qa.grover_algorithm(num_qubits, oracle_function)
print("Grover's Algorithm Output:", result)

// Output
{
  "result": Applying Hadamard gate to qubit 0.
Applying Hadamard gate to qubit 0.
Applying Hadamard gate to qubit 1.
Applying Hadamard gate to qubit 1.
Applying Hadamard gate to qubit 2.
Applying Hadamard gate to qubit 2.
Applying Custom Oracle...
Applying X gate to qubit 0.
Applying Hadamard gate to qubit 1.
Applying X gate to qubit 2.
Applying Hadamard gate to qubit 0.
Applying X gate to qubit 0.
Applying Hadamard gate to qubit 1.
Applying X gate to qubit 1.
Applying Hadamard gate to qubit 2.
Applying X gate to qubit 2.
Custom Oracle Applied.
Applying Diffusion Operator...
Applying Hadamard gate to qubit 0.
Applying X gate to qubit 0.
Applying Hadamard gate to qubit 1.
Applying X gate to qubit 1.
Applying Hadamard gate to qubit 2.
Applying X gate to qubit 2.
Applying Hadamard gate to qubit 2.
Applying X gate to qubit 0.
Applying X gate to qubit 1.
Applying Hadamard gate to qubit 0.
Applying Hadamard gate to qubit 1.
Applying Hadamard gate to qubit 2.
Diffusion Operator Applied.
Running quantum circuit simulation...
Quantum Circuit:
('H', 0)
('H', 1)
('H', 2)
('X', 0)
('H', 1)
('X', 2)
('H', 0)
('X', 0)
('H', 1)
('X', 1)
('H', 2)
('X', 2)
('H', 0)
('X', 0)
('H', 1)
('X', 1)
('H', 2)
('X', 2)
('H', 2)
('X', 0)
('X', 1)
('H', 0)
('H', 1)
('H', 2)
Measuring all qubits...
Grover's Algorithm Output: [0, 0, 0]
}`,

    algorithm3: `// QFT ALGORITHM
from revren_sdk.quantum_algorithms import QuantumAlgorithms
from revren_sdk.quantum_simulator import QuantumSimulator
from revren_sdk.quantum_circuit import QuantumCircuit
num_qubits = 3
result = qa.qft(num_qubits)
print("QFT Output:", result)

// Output
{
  "result":Applying Hadamard gate to qubit 0.
Applying CU1(1.570795) gate with control 1 and target 0.
Applying CU1(0.7853975) gate with control 2 and target 0.
Applying Hadamard gate to qubit 1.
Applying CU1(1.570795) gate with control 2 and target 1.
Applying Hadamard gate to qubit 2.
Swapping qubit 0 with qubit 2.
Running quantum circuit simulation...
Quantum Circuit:
('H', 0)
('CU1', 1.570795, 1, 0)
('CU1', 0.7853975, 2, 0)
('H', 1)
('CU1', 1.570795, 2, 1)
('H', 2)
('SWAP', 0, 2)
Measuring all qubits...
QFT Output: [0, 0, 0]
}`
  };

  return (
    <section id="sdk" className="sdk-section">
      <div className="sdk-container">
        <h3 className="sdk-title" data-aos="fade-up">
          Revren SDK
        </h3>
        
        <div className="sdk-content">
          <div className="sdk-info" data-aos="fade-right">
            <h4>Powerful Development Tools</h4>
            <p>
              Access our quantum computing capabilities through our comprehensive SDK.
              Build and deploy quantum-enhanced applications with ease.
            </p>
            
            <div className="sdk-features">
              <ul>
                <li>Quantum Algorithm Libraries</li>
                <li>AI Integration Tools</li>
                <li>Cloud Deployment Solutions</li>
                <li>Documentation & Support</li>
              </ul>
            </div>
          </div>

          <div className="sdk-demo" data-aos="fade-left">
            <div className="code-tabs">
              <button 
                className={`tab-btn ${activeTab === 'main' ? 'active' : ''}`}
                onClick={() => setActiveTab('main')}
              >
                MAIN EXAMPLE
              </button>
              <button 
                className={`tab-btn ${activeTab === 'algorithm1' ? 'active' : ''}`}
                onClick={() => setActiveTab('algorithm1')}
              >
                SIMON ALGORITHM
              </button>
              <button 
                className={`tab-btn ${activeTab === 'algorithm2' ? 'active' : ''}`}
                onClick={() => setActiveTab('algorithm2')}
              >
                GROVER ALGORITHM
              </button>
              <button 
                className={`tab-btn ${activeTab === 'algorithm3' ? 'active' : ''}`}
                onClick={() => setActiveTab('algorithm3')}
              >
                QFT ALGORITHM
              </button>
            </div>
            <div className="code-output-container">
              <pre className="code-preview">
                {highlightCode(codeExamples[activeTab])}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SDKSection;